if (!customElements.get('products-toolbar')) {
  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.sectionRoot = this.closest('.shopify-section') || document;
      this.filtersComponent = this.sectionRoot.querySelector('facet-filters');
      this.filtersColumn = this.sectionRoot.querySelector('.main-products-grid__filters');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.compareToggle = this.querySelector('#compare-switch') || this.sectionRoot.querySelector('#compare-switch');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = this.sectionRoot.querySelector('.main-products-grid');

      if (this.filtersColumn) {
        this.filterToggle = this.querySelector('.js-toggle-filters');
        this.filterToggle.addEventListener('click', this.toggleFilters.bind(this));
      }

      if (this.layoutSwitcher) {
        this.layoutSwitcher.addEventListener('change', this.handleLayoutChange.bind(this));
        this.mobileLayoutQuery = window.matchMedia('(max-width: 769px)');
        this.handleMobileLayoutChange = this.handleMobileLayoutChange.bind(this);

        if (this.mobileLayoutQuery.addEventListener) {
          this.mobileLayoutQuery.addEventListener('change', this.handleMobileLayoutChange);
        } else {
          this.mobileLayoutQuery.addListener(this.handleMobileLayoutChange);
        }

        if (this.mobileLayoutQuery.matches) {
          this.forceGridLayout();
        } else {
          // Restore a saved desktop preference only while the layout controls are available.
          const layout = theme.storageUtil.get('products-layout');
          if (layout === 'list') {
            const toggle = this.querySelector('#list-layout-opt');
            if (toggle) toggle.checked = true;
            this.handleLayoutChange(null, layout);
          }
        }
      }

      if (this.compareToggle) {
        this.compareToggle.addEventListener('change', this.toggleCompare.bind(this));

        const isChecked = theme.storageUtil.get('compare-toggle', true);
        if (isChecked !== null) {
          this.toggleCompare(null, isChecked, true);
        } else {
          this.toggleCompare(null, this.compareToggle.checked, false);
        }

        setTimeout(() => {
          this.classList.add('transition-ready');
          this.productsGrid.classList.add('transition-ready');
        }, 500);
      } else if (theme.settings.compareToggle === 'none') {
        this.toggleCompare(null, true, false);
      }

      if (!this.filtersComponent && this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    disconnectedCallback() {
      if (!this.mobileLayoutQuery || !this.handleMobileLayoutChange) return;
      if (this.mobileLayoutQuery.removeEventListener) {
        this.mobileLayoutQuery.removeEventListener('change', this.handleMobileLayoutChange);
      } else {
        this.mobileLayoutQuery.removeListener(this.handleMobileLayoutChange);
      }
    }

    /**
     * Updates the UI to reflect the compare state
     * @param {object} evt - Change event object
     * @param {boolean} checked - Whether compare is enabled or not (optional)
     * @param {boolean} persist - Whether to persist the new state (optional)
     */
    toggleCompare(evt, checked = null, persist = true) {
      const isChecked = evt ? evt.target.checked : checked;
      this.productsGrid.dataset.compare = isChecked;

      const compareButton = document.getElementById('compare-drawer-open');
      if (compareButton) compareButton.classList.toggle('is-out', !isChecked);

      if (!evt && this.compareToggle) this.compareToggle.checked = isChecked;

      if (persist && !Shopify.designMode) {
        theme.storageUtil.set('compare-toggle', isChecked);
      }

      // Load scripts/styles if not already loaded
      if (isChecked) {
        const resourcesTag = document.getElementById('compare-resources');
        if (resourcesTag) {
          const resources = JSON.parse(resourcesTag.textContent);
          resources.forEach((resource) => {
            if (resource.type === 'js') {
              const script = document.createElement('script');
              script.src = resource.path;
              document.head.appendChild(script);
            } else if (resource.type === 'css') {
              const link = document.createElement('link');
              link.href = resource.path;
              link.type = 'text/css';
              link.rel = 'stylesheet';
              document.head.appendChild(link);
            }
          });
          resourcesTag.remove();
        }
      }
    }

    /**
     * Toggles open/closed state of the filters on desktop.
     */
    toggleFilters() {
      if (!theme.mediaMatches.lg) {
        if (this.filtersComponent?.drawerIsOpen) this.filtersComponent.close();
        else this.filtersComponent?.open(this.filterToggle);
        return;
      }
      const open = this.filtersColumn.classList.contains('lg:js-hidden');
      this.filtersColumn.classList.toggle('lg:js-hidden', !open);
      this.filtersComponent?.toggleAttribute('open', open);
      this.filtersComponent?.setAttribute('aria-hidden', String(!open));
      this.filterToggle.classList.toggle('is-active', open);
      this.filterToggle.setAttribute('aria-expanded', String(open));
      const productsList = this.sectionRoot.querySelector('.main-products-grid__results > .grid');
      if (productsList) productsList.className = open ? productsList.dataset.filtersOpenClasses : productsList.dataset.filtersClosedClasses;
    }

    /**
     * Keeps mobile/tablet catalogue cards in grid mode once the layout controls disappear.
     */
    handleMobileLayoutChange(evt) {
      if (evt.matches) this.forceGridLayout();
    }

    forceGridLayout() {
      if (!this.productsGrid) return;
      const gridToggle = this.querySelector('#grid-layout-opt');
      const listToggle = this.querySelector('#list-layout-opt');

      if (gridToggle) gridToggle.checked = true;
      if (listToggle) listToggle.checked = false;
      this.productsGrid.dataset.layout = 'grid';

      // Do not allow a desktop list preference to reappear after crossing into mobile.
      if (!Shopify.designMode) theme.storageUtil.remove('products-layout');
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Change event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      const nextLayout = layout || evt.target.value;

      if (this.mobileLayoutQuery?.matches && nextLayout === 'list') {
        this.forceGridLayout();
        return;
      }

      this.productsGrid.dataset.layout = nextLayout;
      if (!layout) theme.storageUtil.set('products-layout', nextLayout);
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
